import React, { useState } from 'react';
import { Send, X, Lock, AlertCircle, FlaskConical } from 'lucide-react';
import { Avatar } from '../ui';
import toast from 'react-hot-toast';
import { submitStudyDraftForApproval } from '../../services/reviewWorkflowApi';

/**
 * "Submit Design for Approval" modal (FECOA-1601).
 *
 * Locks the design from editing once submitted; the submitter can add an
 * optional message to the reviewer, which is stored on the review submission and
 * surfaced to the approver. When the draft was previously denied, the latest
 * reviewer feedback is shown so the submitter can confirm they addressed it.
 *
 * Props:
 *   open           - whether the modal is shown
 *   onClose        - close handler
 *   draftId        - StudyDraft id to submit
 *   draftName      - display name for the study card
 *   denialFeedback - optional latest denial reason (shows the "previously denied" banner)
 *   onSubmitted    - called after a successful submit
 */
const SubmitForApprovalModal = ({ open, onClose, draftId, draftName, denialFeedback, onSubmitted }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!draftId) return;
    setBusy(true);
    try {
      await submitStudyDraftForApproval(draftId, note.trim());
      toast.success('Submitted for approval.');
      onSubmitted && onSubmitted();
    } catch (err) {
      const data = err?.response?.data;
      const detail = Array.isArray(data?.errors) && data.errors.length
        ? `${data.detail || 'This study cannot be submitted yet.'} ${data.errors.join(' ')}`
        : (data?.detail || data?.reason || 'Could not submit for approval.');
      toast.error(detail);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <Send className="h-[18px] w-[18px] flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Submit Design for Approval</h3>
          </div>
          <button
            onClick={() => !busy && onClose()}
            className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="space-y-4 px-6 pb-5 pt-2">
          {/* Study card */}
          <div className="flex items-center gap-3 rounded-lg border border-gray-200/70 bg-gray-50/80 px-4 py-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
            <Avatar icon={FlaskConical} size="sm" />
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">{draftName}</div>
              <div className="text-[12.5px] text-gray-500 dark:text-gray-400">Study Design Configuration</div>
            </div>
          </div>

          {/* Previously-denied feedback */}
          {denialFeedback && (
            <div className="rounded-lg border border-danger-600/15 bg-danger-50/70 px-4 py-3 dark:border-danger-400/20 dark:bg-danger-500/10">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-danger-700 dark:text-danger-400">
                <AlertCircle className="h-4 w-4" />
                Previously denied: Reviewer feedback
              </div>
              <p className="mt-1 text-[13px] italic text-gray-700 dark:text-gray-200">“{denialFeedback}”</p>
            </div>
          )}

          {/* Lock warning */}
          <div className="flex gap-3 rounded-lg border border-gray-200/70 bg-gray-50/80 px-4 py-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
              Once submitted, this study design will be <strong className="font-semibold text-gray-900 dark:text-gray-50">locked from editing</strong> until
              an admin approves or denies the submission. You can withdraw at any time to resume editing.
            </p>
          </div>

          {/* Message to reviewer */}
          <div>
            <label className="label">
              Message to reviewer <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="e.g. All schedule configurations are complete and verified against protocol v2.1. Please pay attention to Stage 2 frequency settings."
              className="input"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            onClick={() => !busy && onClose()}
            disabled={busy}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="btn btn-primary"
          >
            <Send className="h-4 w-4" />
            {busy ? 'Submitting…' : 'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitForApprovalModal;
